// @ts-strict-ignore
import React from 'react';

import {TooltipReact} from 'common/components/tooltip';

import styles from './index.module.styl';
import {RecordingEventMetadata, RecordingInactivePeriod, RecordingMetadata} from './types';
import {getRelevantReplayEvents, getSearchHighlightPieces} from './util';

type RecordingPlayerTimelineProps = {
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  recordingDuration: number;
  recordingProgress: number;
  recordingSkippingInactive: boolean;
  replayInactivePeriods: RecordingInactivePeriod[];
  searchFilter: string;
  updatePlayerProgress: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

export function RecordingPlayerTimeline(props: RecordingPlayerTimelineProps) {
  function getRecordingTimelineWithInactiveSegments(filled = false) {
    const allPeriods = [];
    let startTime = props.replay.time * 1000;
    const endTime = startTime + props.recordingDuration;

    for (const period of props.replayInactivePeriods) {
      allPeriods.push({startTime, endTime: period.startTime});
      allPeriods.push(period);
      startTime = period.endTime;
    }
    allPeriods.push({startTime, endTime});

    return (
      <div className={styles.playerControlsProgressBarSegmentsContainer}>
        {allPeriods.map((period, index) => {
          return (
            <div
              key={index}
              className={
                index % 2 === 0
                  ? styles.playerControlsProgressBarSegmentActive
                  : styles.playerControlsProgressBarSegmentInactive
              }
              style={{
                width: `${(100 * (period.endTime - period.startTime)) / props.recordingDuration}%`,
                backgroundColor: filled
                  ? props.recordingSkippingInactive
                    ? `var(--gray4)`
                    : `var(--blue1)`
                  : `var(--gray2)`,
              }}
            />
          );
        })}
      </div>
    );
  }

  function getRecordingTimelineEvents() {
    let events = getRelevantReplayEvents(props.replayEvents);
    if (props.searchFilter) {
      events = events.filter((event) => {
        return getSearchHighlightPieces(event.eventName, props.searchFilter).some((piece) => piece.highlight);
      });
    }
    const progressPercentage = Math.min(100 * (props.recordingProgress / props.recordingDuration), 100);

    return (
      <div className={styles.playerControlsProgressBarEvents}>
        {events.map((event, index) => {
          const eventTimeOffset = (event.eventTime - props.replay.time) * 1000;
          const eventOffsetPercentage = Math.min((100 * eventTimeOffset) / props.recordingDuration, 100);
          const left = `${eventOffsetPercentage}%`;
          return (
            <div
              key={index}
              className={`${styles.playerControlsProgressBarEvent} ${progressPercentage < eventOffsetPercentage ? styles.playerControlsProgressBarEventUnplayed : styles.playerControlsProgressBarEventPlayed}`}
              style={{left}}
            >
              <TooltipReact>{event.eventName}</TooltipReact>
            </div>
          );
        })}
      </div>
    );
  }

  if (!props.replay) {
    return null;
  }

  const cutoffPercentage =
    props.recordingDuration !== 0
      ? 100 - Math.min(100 * (props.recordingProgress / props.recordingDuration), 100)
      : 100;

  return (
    <div className={styles.playerControlsProgressBar} onClick={(event) => props.updatePlayerProgress(event)}>
      <div className={styles.playerControlsProgressBarEmpty}>{getRecordingTimelineWithInactiveSegments()}</div>
      <div
        className={styles.playerControlsProgressBarFilled}
        style={{
          clipPath: `inset(0% ${cutoffPercentage}% 0% 0%)`,
        }}
      >
        {getRecordingTimelineWithInactiveSegments(true)}
      </div>
      {getRecordingTimelineEvents()}
      <div
        className={styles.playerControlsProgressBarIndicator}
        style={
          props.recordingSkippingInactive
            ? {backgroundColor: `var(--gray4)`, left: `${100 - cutoffPercentage}%`}
            : {left: `${100 - cutoffPercentage}%`}
        }
      />
    </div>
  );
}
